import {
  Close as DialogClose,
  Content as DialogContent,
  Overlay as DialogOverlay,
  Portal as DialogPortal,
  Root as DialogRoot,
  Title as DialogTitle,
} from '@radix-ui/react-dialog';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  PropsWithChildren,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import IconButton from './button/IconButton';

type ModalProps = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  className?: string;
}>;
function Modal({ open, onClose, children, className }: ModalProps) {
  const content = useRef<HTMLDivElement>(null);
  const [showCloseMobile, setShowCloseMobile] = useState(false);

  return (
    <DialogRoot
      modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <DialogPortal>
        <DialogOverlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-scrim/32 backdrop-blur-xs" />
        <DialogContent
          tabIndex={-1}
          ref={content}
          onOpenAutoFocus={(e) => {
            // focus the content container instead of the first focusable element
            e.preventDefault();
            content.current?.focus();
          }}
          onTouchStart={() => setShowCloseMobile(true)}
          className={twMerge(
            'group data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 duration-200',
            'grid max-h-full w-full max-w-lg grid-flow-dense gap-4 overflow-y-auto rounded-lg rounded-t-lg border border-outline bg-surface-container-lowest p-6 shadow-lg',
            className,
          )}
          //! setting aria-describedby to undefined must use the Modal title
          aria-describedby={undefined}
        >
          {children}
          <DialogClose asChild>
            <IconButton
              icon="XMark"
              aria-label="Close"
              className={twMerge(
                // overrides
                'bg-surface-container-lowest hover:brightness-100',
                'absolute top-2 right-2 p-2 text-on-surface-variant',
                'opacity-0 transition-opacity group-hover:opacity-50 group-hover:delay-300 hover:bg-surface-container-lowest hover:opacity-100 hover:delay-0 focus:opacity-100 focus:delay-0',
                showCloseMobile && 'opacity-100',
              )}
            />
          </DialogClose>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}

function ModalHeader({
  children,
  className,
}: PropsWithChildren<{
  className?: string;
}>) {
  return (
    <div className={twMerge('flex justify-between', className)}>{children}</div>
  );
}

const ModalTitle = forwardRef<
  ElementRef<typeof DialogTitle>,
  ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, ...props }, ref) => (
  <DialogTitle
    ref={ref}
    className={twMerge('heading-sm text-on-surface', className)}
    // oxlint-disable-next-line react/jsx-props-no-spreading -- modal title props are the same as the dialog title
    {...props}
  />
));
ModalTitle.displayName = 'ModalTitle';

function ModalFooter({
  children,
  className,
}: PropsWithChildren<{
  className?: string;
}>) {
  return (
    <div className={twMerge('flex justify-end gap-4', className)}>
      {children}
    </div>
  );
}

export { Modal, ModalFooter, ModalHeader, ModalTitle };
